import { TWorkflowDefinition } from '@commenda/types';
import { ReactFlowProvider } from '@xyflow/react';
import { v4 as uuidV4 } from 'uuid';

import {
  FormBuilderLinksProvider,
  IFormBuilderLinks,
} from '../../context/FormBuilderLinksContext';
import { WorkflowProvider } from '../../context/WorkflowContext';
import { WorkflowTaskTemplateProvider } from '../../context/WorkflowTaskTemplateContext';

interface WorkflowEditorShellProps {
  children: React.ReactNode;
  initialWorkflow?: TWorkflowDefinition;
  /** Given, the form step panel offers to open and create forms in the host's builder. */
  formBuilderLinks?: IFormBuilderLinks;
  /** Given, the step and group panels offer the task keys the workflow's links bind it to. */
  workflowTemplateId?: string;
}

/** Every provider the editor needs, with the layout left to the caller. `WorkflowEditor` is the
 * one-call version and is what most hosts want; this is for a host that has to put its own chrome
 * between the providers and the canvas, such as a full-height side column that reads canvas state
 * through `useWorkflowCanvasBinding`. Pair it with `WorkflowEditorContent`. */
export const WorkflowEditorShell = ({
  children,
  initialWorkflow,
  formBuilderLinks,
  workflowTemplateId,
}: WorkflowEditorShellProps) => {
  // Create default workflow if none provided
  const defaultWorkflow: TWorkflowDefinition = initialWorkflow || {
    id: uuidV4(),
    name: 'New Workflow',
    steps: [],
    stepTransitions: [],
  };

  const canvas = <ReactFlowProvider>{children}</ReactFlowProvider>;
  const shellContent = workflowTemplateId ? (
    <WorkflowTaskTemplateProvider workflowTemplateId={workflowTemplateId}>
      {canvas}
    </WorkflowTaskTemplateProvider>
  ) : (
    canvas
  );

  return (
    <WorkflowProvider initialWorkflow={defaultWorkflow}>
      {formBuilderLinks ? (
        <FormBuilderLinksProvider links={formBuilderLinks}>
          {shellContent}
        </FormBuilderLinksProvider>
      ) : (
        shellContent
      )}
    </WorkflowProvider>
  );
};
